import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/component/navbar';

type BusinessType = 'freezone' | 'mainland';

const ProItem = ({ text }: { text: string }) => (
  <div className="flex items-start gap-2.5">
    <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
    <span className="text-sm text-gray-700 leading-snug">{text}</span>
  </div>
);

const ConItem = ({ text }: { text: string }) => (
  <div className="flex items-start gap-2.5">
    <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
    <span className="text-sm text-gray-700 leading-snug">{text}</span>
  </div>
);

const RadioCircle = ({ selected }: { selected: boolean }) => (
  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? 'border-amber-500' : 'border-gray-400 bg-white'}`}>
    {selected && <div className="w-3 h-3 rounded-full bg-amber-500" />}
  </div>
);

export default function SetupBusinessType() {
  const [selected, setSelected] = useState<BusinessType | null>(null);
  const [hovered, setHovered] = useState<BusinessType | null>(null);
  const navigate = useNavigate();

  const handleNext = () => {
    if (!selected) return;
    localStorage.setItem('setupBusinessType', selected);
    navigate('/setup-company-name');
  };

  const cards = [
    {
      id: 'freezone' as BusinessType,
      title: 'Free Zone Company',
      subtitle: '(FZ-LLC)',
      description: 'For startups, e-commerce, and SMEs who want fast, flexible setup.',
      pros: [
        '100% foreign ownership',
        'Quick setup (some Free Zones incorporate in days)',
        'Lower cost options (flexi-desk, bundled packages)',
        'No local sponsor required',
      ],
      cons: [
        'Limited to Free Zone & international trade (need extra permit to serve mainland clients)',
        'Activity scope depends on Free Zone',
      ],
    },
    {
      id: 'mainland' as BusinessType,
      title: 'Mainland Company',
      subtitle: '(LLC)',
      description: 'For businesses targeting the wider UAE market.',
      pros: [
        '100% foreign ownership (for most activities)',
        'Operate anywhere in UAE + internationally',
        'Access to government contracts & large corporates',
        'Wider licensing options (professional/ commercial/industrial)',
      ],
      cons: [
        'Higher costs (office lease + government fees)',
        'More paperwork & ongoing compliance',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar hideSignBtn={false} />

      <div className="flex-1 px-8 py-10 max-w-5xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">
          What kind of business are you building?
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {cards.map((card) => {
            const isSelected = selected === card.id;
            const isHovered = hovered === card.id;
            return (
              <div
                key={card.id}
                onClick={() => setSelected(card.id)}
                onMouseEnter={() => setHovered(card.id)}
                onMouseLeave={() => setHovered(null)}
                className="rounded-xl p-6 cursor-pointer transition-all duration-200"
                style={{
                  backgroundColor: isSelected ? '#FFFBEB' : isHovered ? '#FEFCE8' : 'white',
                  border: isSelected
                    ? '2px solid #F59E0B'
                    : isHovered
                    ? '2px solid #FDE68A'
                    : '2px solid #E5E7EB',
                  boxShadow: isSelected
                    ? '0 4px 20px rgba(245,158,11,0.15)'
                    : '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                <h2 className="text-xl font-bold text-gray-900 leading-snug">{card.title}</h2>
                <h2 className="text-xl font-bold text-gray-900 mb-3">{card.subtitle}</h2>
                <p className="text-sm font-semibold text-gray-800 mb-4 leading-relaxed">
                  {card.description}
                </p>
                <hr className="border-gray-200 mb-4" />
                <div className="space-y-2.5 mb-5">
                  {card.pros.map((p, i) => <ProItem key={i} text={p} />)}
                  {card.cons.map((c, i) => <ConItem key={i} text={c} />)}
                </div>
                <div className="flex justify-end">
                  <RadioCircle selected={isSelected} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-8 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!selected}
            className="px-8 py-3 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
